import i18n from "i18next";
import { AavoObjectRef } from "src/api/generated/common/aavoObject/aavoObjectRef.ts";
import { DocumentsOfSingleObjectView } from "src/components/views/documents/objectDocuments/DocumentsOfSingleObjectView.tsx";
import { ProductChangeRequestCatalogPartsDataGrid } from "src/components/views/erp/productChanges/requests/catalogParts/ProductChangeRequestCatalogPartsDataGrid.tsx";
import type {
	ProductChangeRequestDialogMode,
	ProductChangeRequestFormProps,
} from "src/components/views/erp/productChanges/requests/ProductChangeRequestForm.tsx";
import { ProductChangeRequestForm } from "src/components/views/erp/productChanges/requests/ProductChangeRequestForm.tsx";
import { AavoMosaic } from "src/components/common/mosaic/AavoMosaic.tsx";
import { SingleSourceTasksView } from "../../../tasks/pages/SingleSourceTasksView.tsx";
import { ObjectChangeLogDataGrid } from "../../../changeLogging/ObjectChangeLogDataGrid.tsx";
import { WorkspacesView } from "../../../workspaces/WorkspacesView.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { useTasksEnabled } from "src/components/views/tasks/useTasksEnabled.ts";

export type { ProductChangeRequestDialogMode };

export type ProductChangeRequestDialogProps = ProductChangeRequestFormProps;

export const ProductChangeRequestDialog = ({
	productChangeRequestId,
	mode,
	onFormEdited,
	onCompleted,
}: ProductChangeRequestDialogProps) => {
	const { tenantConfig } = useTenantCustomizations();
	const tasksEnabled = useTasksEnabled();
	const canManage = mode === "manage";

	const objectRef: AavoObjectRef = {
		objectType: "PRODUCT_CHANGE_REQUEST",
		objectId: productChangeRequestId,
	};

	return (
		<AavoMosaic
			layout={{
				type: "tabs",
				items: [
					{
						type: "panel",
						title: i18n.t("product_change_request"),
						content: (
							<ProductChangeRequestForm
								productChangeRequestId={productChangeRequestId}
								mode={mode}
								onFormEdited={onFormEdited}
								onCompleted={onCompleted}
							/>
						),
					},
					tenantConfig.documentsEnabled && {
						type: "panel",
						title: i18n.t("documents"),
						content: <DocumentsOfSingleObjectView objectRef={objectRef} editable />,
					},
					tasksEnabled &&
						canManage && {
							type: "panel",
							title: i18n.t("tasks"),
							content: (
								<SingleSourceTasksView
									taskSourceRef={{
										sourceType: "PRODUCT_CHANGE_REQUEST",
										sourceId: productChangeRequestId,
									}}
								/>
							),
						},
					canManage && {
						type: "panel",
						title: i18n.t("parts"),
						content: (
							<ProductChangeRequestCatalogPartsDataGrid productChangeRequestId={productChangeRequestId} />
						),
					},
					tenantConfig.workspacesEnabled &&
						canManage && {
							type: "panel",
							title: i18n.t("workspaces"),
							content: (
								<WorkspacesView
									onlyWorkspacesConnectedToItem={{
										itemType: "PRODUCT_CHANGE_REQUEST",
										itemId: productChangeRequestId,
									}}
								/>
							),
						},
					canManage && {
						type: "panel",
						title: i18n.t("change_log"),
						content: <ObjectChangeLogDataGrid objectRef={objectRef} />,
					},
				],
			}}
		/>
	);
};
